/**
 * System metrics utilities for tracking browser memory usage.
 * These metrics help identify performance issues and memory leaks.
 */

import { getOpenTabCount } from './tabCounter';

/**
 * Gets available browser memory statistics.
 * Note: performance.memory is only available in Chrome/Edge with --enable-precise-memory-info flag.
 * Returns undefined values for unsupported browsers.
 */
export const getMemoryStats = () => {
  const stats: {
    heapUsedMB?: number;
    heapTotalMB?: number;
    heapLimitMB?: number;
    heapUsedPercent?: number;
    deviceMemoryGB?: number;
  } = {};

  // Chrome/Edge only - JavaScript heap memory
  if ('memory' in performance) {
    const memory = (performance as any).memory;
    if (memory.usedJSHeapSize && memory.totalJSHeapSize && memory.jsHeapSizeLimit) {
      stats.heapUsedMB = Math.round(memory.usedJSHeapSize / 1048576);
      stats.heapTotalMB = Math.round(memory.totalJSHeapSize / 1048576);
      stats.heapLimitMB = Math.round(memory.jsHeapSizeLimit / 1048576);
      stats.heapUsedPercent = Math.round(
        (memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100
      );
    }
  }

  // Approximate device memory (experimental API)
  // Returns number of GB (e.g., 4, 8, 16)
  if ('deviceMemory' in navigator) {
    stats.deviceMemoryGB = (navigator as any).deviceMemory;
  }

  return stats;
};

/**
 * Gets CPU-related statistics.
 * Note: Browsers don't expose actual CPU usage for security reasons.
 * This only provides the number of logical processors.
 */
export const getCPUStats = () => {
  const stats: {
    logicalProcessors?: number;
  } = {};

  // Number of logical CPU cores
  if ('hardwareConcurrency' in navigator) {
    stats.logicalProcessors = navigator.hardwareConcurrency;
  }

  return stats;
};

/**
 * Gets all available system metrics in one call.
 * Includes memory stats, CPU core count, and open tab count.
 */
export const getSystemMetrics = () => {
  return {
    ...getMemoryStats(),
    ...getCPUStats(),
    openTabCount: getOpenTabCount(),
  };
};
